import { useState } from 'react';
import type { FormEvent } from 'react';
import { Dialog, Input, Button } from '@/design-system';
import * as dashboardApi from '@/features/dashboard/api/dashboard-api';
import { TEMPLATE_MAX_TAGS } from '@/contracts/templates';

export interface SaveAsTemplateDialogProps {
  open: boolean;
  funnelId: string;
  funnelName: string;
  flush: () => Promise<boolean>;
  onClose: () => void;
}

function parseTags(raw: string): string[] {
  return raw
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean)
    .slice(0, TEMPLATE_MAX_TAGS);
}

/**
 * SPEC-010 §50/§51/§54/plan §19-20 — the dirty editor is flushed *before*
 * the server reads the persisted graph (never captures unsaved state
 * silently). A failed flush blocks template creation with an explanation
 * instead of templating a stale/local-only revision.
 */
export function SaveAsTemplateDialog({ open, funnelId, funnelName, flush, onClose }: SaveAsTemplateDialogProps) {
  const [name, setName] = useState(funnelName);
  const [description, setDescription] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const flushed = await flush();
      if (!flushed) {
        setError('Não foi possível salvar suas últimas alterações antes de criar o modelo. Tente novamente quando estiver on-line.');
        return;
      }
      await dashboardApi.createCustomTemplate({
        funnelId,
        name: name.trim(),
        description: description.trim(),
        tags: parseTags(tagsInput),
      });
      onClose();
    } catch {
      setError('Não foi possível salvar este funil como modelo. Tente novamente.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="Salvar como modelo">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <Input label="Nome do modelo" value={name} onChange={(e) => setName(e.target.value)} autoFocus required disabled={submitting} />
        <label className="flex flex-col gap-1 text-sm text-text-secondary">
          Descrição
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={3}
            disabled={submitting}
            className="rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary"
          />
        </label>
        <Input
          label="Etiquetas (separadas por vírgula)"
          value={tagsInput}
          onChange={(e) => setTagsInput(e.target.value)}
          disabled={submitting}
        />
        {error ? (
          <p role="alert" className="text-sm text-[#f08484]">
            {error}
          </p>
        ) : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button type="submit" disabled={submitting || name.trim().length === 0}>
            {submitting ? 'Salvando…' : 'Salvar como modelo'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
